import React from 'react';
import { useTranslation } from 'react-i18next';
import { isProcessingStatus } from '../constants/status';

interface StatusBadgeProps {
  status: string;
  showSpinner?: boolean;
  /** 运行态默认带呼吸动画；个别页面不想动就传 false */
  pulse?: boolean;
  label?: string;        // override display text
  style?: React.CSSProperties;
}

const STATUS_CLASS_MAP: Record<string, string> = {
  pending: 'status-pending',
  draft: 'status-pending',
  pending_normalization: 'status-pending',
  pending_pronoun_resolve: 'status-pending',
  resolving_pronouns_failed: 'status-failed',
  uploading: 'status-uploading',
  uploading_to_tos: 'status-uploading',
  uploaded: 'status-uploading',
  splitting: 'status-splitting',
  downloading_episodes: 'status-splitting',
  uploading_episodes_to_tos: 'status-splitting',
  normalizing: 'status-splitting',
  normalizing_failed: 'status-failed',
  resolving_pronouns: 'status-splitting',
  generating: 'status-generating',
  generating_shots: 'status-generating',
  completed: 'status-completed',
  failed: 'status-failed',
  cancelling: 'status-pending',
  cancelled: 'status-failed',
};

const STATUS_TEXT_KEYS: Record<string, string> = {
  draft: 'statusBadge.draft',
  pending: 'statusBadge.pending',
  pending_normalization: 'statusBadge.pending_normalization',
  pending_pronoun_resolve: 'statusBadge.pending_pronoun_resolve',
  resolving_pronouns_failed: 'statusBadge.resolving_pronouns_failed',
  uploading: 'statusBadge.uploading',
  uploading_to_tos: 'statusBadge.uploading_to_tos',
  uploaded: 'statusBadge.uploaded',
  splitting: 'statusBadge.splitting',
  downloading_episodes: 'statusBadge.downloading_episodes',
  uploading_episodes_to_tos: 'statusBadge.uploading_episodes_to_tos',
  normalizing: 'statusBadge.normalizing',
  normalizing_failed: 'statusBadge.normalizing_failed',
  resolving_pronouns: 'statusBadge.resolving_pronouns',
  generating: 'statusBadge.generating',
  generating_shots: 'statusBadge.generating_shots',
  completed: 'statusBadge.completed',
  failed: 'statusBadge.failed',
  cancelling: 'statusBadge.cancelling',
  cancelled: 'statusBadge.cancelled',
};

const SPINNER_STATUSES = new Set(['generating', 'generating_shots', 'splitting', 'normalizing', 'resolving_pronouns', 'pending_normalization', 'pending_pronoun_resolve', 'cancelling']);

const StatusBadge: React.FC<StatusBadgeProps> = ({ status, showSpinner, pulse, label, style }) => {
  const { t } = useTranslation('common');
  const cssClass = STATUS_CLASS_MAP[status] || 'status-pending';
  const textKey = STATUS_TEXT_KEYS[status];
  const text = label || (textKey ? t(textKey) : status);
  const shouldSpin = showSpinner !== undefined ? showSpinner : SPINNER_STATUSES.has(status);
  const shouldPulse = pulse !== false && isProcessingStatus(status);

  return (
    <span className={`status-badge ${cssClass}${shouldPulse ? ' status-pulse' : ''}`} style={style}>
      {shouldSpin && <span className="loading-spinner" />}
      {text}
    </span>
  );
};

export default StatusBadge;
